import { inject, Injectable } from '@angular/core';
import { Tokens } from '@auth/models/tokens';
import { CurrentUserInfo } from '@core/models/current-user-info';
import { Storage } from '@core/services/storage';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { CityApi } from './city-api';
import { City } from '@core/models/city';
import { Role } from '@auth/models/role';
import { VolunteerApi } from '@administration/services/volunteer-api';
import { VolunteerDetails } from '@administration/models/volunteer-details';

const TOKENS_KEY = 'tokens';
const UNKNOWN_CITY: City = { id: '0', name: 'Unknown' };

interface TokenInfo {
  id: string;
  userName: string;
  roles: Role[];
}

export const getTokenInfo = (accessToken: string): TokenInfo => {
  const payload = JSON.parse(window.atob(accessToken.split('.')[1]));
  const roles = ([] as string[]).concat(payload['role'] ?? []) as Role[];

  return {
    id: String(payload['nameid']),
    userName: String(payload['unique_name']),
    roles,
  };
};

const createUserInfo = (cities: City[], volunteer: VolunteerDetails, tokenInfo: TokenInfo): CurrentUserInfo => ({
  ...tokenInfo,
  city: cities.find((c) => c.id === volunteer.cityId) ?? UNKNOWN_CITY,
});

@Injectable({
  providedIn: 'root',
})
export class UserInfo {
  private readonly storage = inject(Storage);
  private readonly cityApi = inject(CityApi);
  private readonly volunteerApi = inject(VolunteerApi);

  private readonly cities$ = this.cityApi.getCities().pipe(shareReplay(1));

  private readonly tokensSubject = new BehaviorSubject<Tokens | null>(this.getTokens());

  public readonly authenticated$ = this.tokensSubject.asObservable().pipe(distinctUntilChanged(), map(Boolean));
  public readonly user$ = this.tokensSubject.pipe(
    switchMap((tokens) => {
      if (!tokens) {
        return of(null);
      }

      const infos$ = forkJoin({
        cities: this.cities$,
        volunteer: this.volunteerApi.getCurrentVolunteer(),
      });

      return infos$.pipe(
        map(({ cities, volunteer }) => {
          const info = getTokenInfo(tokens.accessToken);

          return createUserInfo(cities, volunteer, info);
        }),
        catchError(() => of(null)),
      );
    }),
    filter(Boolean),
    shareReplay(1),
  );

  public getAccessToken(): string | null {
    return this.tokensSubject.value?.accessToken || null;
  }

  public authenticate(tokens: Tokens): void {
    this.storage.setItem(TOKENS_KEY, tokens);

    this.tokensSubject.next(tokens);
  }

  public clear(): void {
    this.storage.removeItem(TOKENS_KEY);

    this.tokensSubject.next(null);
  }

  private getTokens(): Tokens | null {
    return this.storage.getItem<Tokens>(TOKENS_KEY);
  }
}
